// Automation Studio Mock Data - Extends Vedaya with safe sys admin automation

export const connectorRegistry = [
  { id: 'aws', name: 'AWS', version: '2.4.1', status: 'active', lastSync: '2 min ago', entities: 1247, actions: 12 },
  { id: 'okta', name: 'Okta', version: '1.8.0', status: 'active', lastSync: '5 min ago', entities: 1523, actions: 8 },
  { id: 'github', name: 'GitHub', version: '3.1.2', status: 'active', lastSync: '1 min ago', entities: 892, actions: 6 },
  { id: 'jira', name: 'Jira', version: '2.0.0', status: 'active', lastSync: '10 min ago', entities: 4521, actions: 5 },
  { id: 'slack', name: 'Slack', version: '1.5.3', status: 'active', lastSync: '3 min ago', entities: 1247, actions: 4 },
  { id: 'intune', name: 'Microsoft Intune', version: '1.2.0', status: 'active', lastSync: '8 min ago', entities: 2341, actions: 10 },
  { id: 'crowdstrike', name: 'CrowdStrike', version: '1.0.1', status: 'active', lastSync: '4 min ago', entities: 2341, actions: 6 },
  { id: 'snowflake', name: 'Snowflake', version: '0.9.0', status: 'beta', lastSync: '15 min ago', entities: 156, actions: 3 },
  { id: 'workday', name: 'Workday', version: '1.1.0', status: 'pending', lastSync: 'Never', entities: 0, actions: 7 }
];

export const connectorSDKContract = {
  methods: [
    { name: 'discover()', description: 'Enumerate all entities (users, devices, resources) from the source system', returns: 'Entity[]' },
    { name: 'normalize()', description: 'Transform raw entities into Vedaya canonical schema', returns: 'NormalizedEntity[]' },
    { name: 'act(action, params)', description: 'Execute a safe, idempotent action on the source system', returns: 'ActionResult' },
    { name: 'audit()', description: 'Emit structured audit events for all operations', returns: 'AuditEvent[]' }
  ],
  authTypes: ['OAuth 2.0', 'SAML', 'API Key', 'Service Principal'],
  actionWrappers: [
    { name: 'createUser', description: 'Provision a new user account' },
    { name: 'disableUser', description: 'Disable user access (soft delete)' },
    { name: 'wipeDevice', description: 'Remote wipe a managed device' },
    { name: 'reclaimLicense', description: 'Revoke and reclaim unused license' },
    { name: 'rotateCredential', description: 'Rotate API keys or secrets' },
    { name: 'updatePermission', description: 'Modify user or group permissions' }
  ]
};

export const generatedConnectorCode = `// Generated Connector: Acme HR System
// Auth Type: OAuth 2.0
// Generated: January 11, 2026

import { Connector, Entity, Action, AuditEvent } from '@vedaya/sdk';
import { OAuth2Handler } from '@vedaya/auth';
import { withRetry, withPagination } from '@vedaya/utils';

export class AcmeHRConnector extends Connector {
  private auth: OAuth2Handler;
  
  constructor(config: ConnectorConfig) {
    super(config);
    this.auth = new OAuth2Handler({
      clientId: config.clientId,
      clientSecret: config.clientSecret,
      tokenUrl: 'https://acme.example.com/oauth/token',
      scopes: ['users:read', 'users:write']
    });
  }

  @withRetry({ maxAttempts: 3, backoff: 'exponential' })
  @withPagination({ pageSize: 100 })
  async discover(): Promise<Entity[]> {
    const token = await this.auth.getAccessToken();
    const response = await this.client.get('/api/v1/users', {
      headers: { Authorization: \`Bearer \${token}\` }
    });
    return response.data.users;
  }

  normalize(raw: any): NormalizedEntity {
    return {
      id: raw.employee_id,
      type: 'user',
      displayName: \`\${raw.first_name} \${raw.last_name}\`,
      email: raw.work_email,
      status: raw.employment_status === 'active' ? 'active' : 'inactive',
      department: raw.department_name,
      manager: raw.manager_id,
      startDate: raw.hire_date,
      attributes: {
        title: raw.job_title,
        location: raw.office_location,
        costCenter: raw.cost_center
      }
    };
  }

  @withRetry({ maxAttempts: 3 })
  async act(action: Action): Promise<ActionResult> {
    const idempotencyKey = \`\${action.type}-\${action.targetId}-\${Date.now()}\`;
    
    switch (action.type) {
      case 'disableUser':
        return this.disableUser(action.targetId, idempotencyKey);
      case 'createUser':
        return this.createUser(action.params, idempotencyKey);
      default:
        throw new Error(\`Unknown action: \${action.type}\`);
    }
  }

  audit(event: AuditEvent): void {
    this.emit('audit', {
      timestamp: new Date().toISOString(),
      connector: 'acme-hr',
      ...event
    });
  }
}`;

export const workflowTemplates = [
  {
    id: 'onboarding',
    name: 'Employee Onboarding',
    description: 'Provision accounts across all systems for new hires',
    category: 'Identity',
    steps: [
      { id: 1, name: 'Create Okta account', connector: 'Okta', action: 'createUser', retry: 3, compensation: 'deleteUser' },
      { id: 2, name: 'Provision Google Workspace', connector: 'Google', action: 'createUser', retry: 3, compensation: 'deleteUser' },
      { id: 3, name: 'Add to GitHub org', connector: 'GitHub', action: 'addOrgMember', retry: 2, compensation: 'removeOrgMember' },
      { id: 4, name: 'Create Slack account', connector: 'Slack', action: 'inviteUser', retry: 2, compensation: 'deactivateUser' },
      { id: 5, name: 'Assign base licenses', connector: 'Vedaya', action: 'assignLicenses', retry: 2, compensation: 'revokeLicenses' },
      { id: 6, name: 'Send welcome email', connector: 'Email', action: 'sendTemplate', retry: 3, compensation: null }
    ],
    approvalRequired: true,
    approvers: ['HR Manager', 'IT Manager'],
    changeWindow: 'Business hours',
    estimatedDuration: '15 min'
  },
  {
    id: 'offboarding',
    name: 'Employee Offboarding',
    description: 'Revoke access and reclaim licenses for departing employees',
    category: 'Identity',
    steps: [
      { id: 1, name: 'Disable Okta account', connector: 'Okta', action: 'disableUser', retry: 3, compensation: 'enableUser' },
      { id: 2, name: 'Revoke GitHub access', connector: 'GitHub', action: 'removeOrgMember', retry: 2, compensation: 'addOrgMember' },
      { id: 3, name: 'Deactivate Slack', connector: 'Slack', action: 'deactivateUser', retry: 2, compensation: 'reactivateUser' },
      { id: 4, name: 'Suspend Google Workspace', connector: 'Google', action: 'suspendUser', retry: 3, compensation: 'unsuspendUser' },
      { id: 5, name: 'Transfer ownership', connector: 'Google', action: 'transferOwnership', retry: 2, compensation: null },
      { id: 6, name: 'Reclaim all licenses', connector: 'Vedaya', action: 'reclaimLicenses', retry: 2, compensation: null },
      { id: 7, name: 'Archive user data', connector: 'Vedaya', action: 'archiveUserData', retry: 3, compensation: null }
    ],
    approvalRequired: true,
    approvers: ['HR Manager', 'Security Team'],
    changeWindow: 'Any time',
    estimatedDuration: '20 min'
  },
  {
    id: 'patching',
    name: 'Security Patching',
    description: 'Deploy security patches to managed devices',
    category: 'Security',
    steps: [
      { id: 1, name: 'Identify vulnerable devices', connector: 'CrowdStrike', action: 'scanVulnerabilities', retry: 2, compensation: null },
      { id: 2, name: 'Create patch deployment', connector: 'Intune', action: 'createDeployment', retry: 3, compensation: 'cancelDeployment' },
      { id: 3, name: 'Notify device owners', connector: 'Slack', action: 'sendNotification', retry: 2, compensation: null },
      { id: 4, name: 'Deploy patches', connector: 'Intune', action: 'deployPatches', retry: 3, compensation: 'rollbackPatches' },
      { id: 5, name: 'Verify compliance', connector: 'CrowdStrike', action: 'verifyCompliance', retry: 3, compensation: null }
    ],
    approvalRequired: true,
    approvers: ['Security Team', 'Change Advisory Board'],
    changeWindow: 'Maintenance window (2 AM - 6 AM)',
    estimatedDuration: '2 hours'
  },
  {
    id: 'quarantine',
    name: 'Device Quarantine',
    description: 'Isolate compromised or non-compliant devices',
    category: 'Security',
    steps: [
      { id: 1, name: 'Isolate from network', connector: 'CrowdStrike', action: 'networkContain', retry: 3, compensation: 'networkRelease' },
      { id: 2, name: 'Disable VPN access', connector: 'Okta', action: 'revokeVPNAccess', retry: 2, compensation: 'grantVPNAccess' },
      { id: 3, name: 'Revoke OAuth tokens', connector: 'Okta', action: 'revokeAllTokens', retry: 3, compensation: null },
      { id: 4, name: 'Notify security team', connector: 'PagerDuty', action: 'createIncident', retry: 2, compensation: null },
      { id: 5, name: 'Notify device owner', connector: 'Slack', action: 'sendDM', retry: 2, compensation: null }
    ],
    approvalRequired: false,
    approvers: [],
    changeWindow: 'Any time (emergency)',
    estimatedDuration: '5 min'
  },
  {
    id: 'license-reclaim',
    name: 'License Reclamation',
    description: 'Identify and reclaim unused software licenses',
    category: 'Cost',
    steps: [
      { id: 1, name: 'Identify inactive users', connector: 'Vedaya', action: 'findInactiveUsers', retry: 2, compensation: null },
      { id: 2, name: 'Send usage warning', connector: 'Slack', action: 'sendWarning', retry: 2, compensation: null },
      { id: 3, name: 'Wait for response', connector: 'Vedaya', action: 'waitPeriod', retry: 1, compensation: null },
      { id: 4, name: 'Revoke licenses', connector: 'Target App', action: 'revokeLicense', retry: 3, compensation: 'grantLicense' },
      { id: 5, name: 'Update inventory', connector: 'Vedaya', action: 'updateInventory', retry: 2, compensation: null }
    ],
    approvalRequired: true,
    approvers: ['IT Manager', 'Finance'],
    changeWindow: 'Business hours',
    estimatedDuration: '7 days (includes wait period)'
  }
];

export const workflowRuns = [
  {
    id: 'WR-104',
    workflowId: 'offboarding',
    workflowName: 'Employee Offboarding',
    target: 'Alex Rodriguez',
    targetEmail: 'alex.rodriguez@company.com',
    status: 'completed',
    startedAt: '2026-01-10T14:30:00Z',
    completedAt: '2026-01-10T14:48:00Z',
    duration: '18 min',
    initiatedBy: 'HR System (Workday)',
    approvedBy: ['Lisa Park (HR)', 'Jordan Lee (Security)'],
    stepsCompleted: 7,
    stepsTotal: 7,
    dryRun: false,
    idempotencyKey: 'offboard-alex-rodriguez-20260110',
    auditEvents: 14,
    evidenceBundleId: 'EB-2026-0142'
  },
  {
    id: 'WR-103',
    workflowId: 'license-reclaim',
    workflowName: 'License Reclamation',
    target: 'Figma (23 licenses)',
    status: 'completed',
    startedAt: '2026-01-08T09:00:00Z',
    completedAt: '2026-01-08T09:15:00Z',
    duration: '15 min',
    initiatedBy: 'Sarah Chen',
    approvedBy: ['Mike Johnson (IT)', 'Finance Team'],
    stepsCompleted: 5,
    stepsTotal: 5,
    dryRun: false,
    idempotencyKey: 'reclaim-figma-20260108',
    auditEvents: 8,
    evidenceBundleId: 'EB-2026-0138'
  },
  {
    id: 'WR-102',
    workflowId: 'quarantine',
    workflowName: 'Device Quarantine',
    target: 'LT-1042 (Emily Zhang)',
    status: 'completed',
    startedAt: '2026-01-07T16:45:00Z',
    completedAt: '2026-01-07T16:50:00Z',
    duration: '5 min',
    initiatedBy: 'CrowdStrike Alert',
    approvedBy: ['Auto-approved (Emergency)'],
    stepsCompleted: 5,
    stepsTotal: 5,
    dryRun: false,
    idempotencyKey: 'quarantine-lt1042-20260107',
    auditEvents: 10,
    evidenceBundleId: 'EB-2026-0134'
  },
  {
    id: 'WR-101',
    workflowId: 'onboarding',
    workflowName: 'Employee Onboarding',
    target: 'New Hire: Jordan Kim',
    status: 'in_progress',
    startedAt: '2026-01-11T10:00:00Z',
    completedAt: null,
    duration: '5 min (running)',
    initiatedBy: 'HR System (Workday)',
    approvedBy: ['HR Manager'],
    stepsCompleted: 3,
    stepsTotal: 6,
    dryRun: false,
    idempotencyKey: 'onboard-jordan-kim-20260111',
    auditEvents: 6,
    evidenceBundleId: null
  },
  {
    id: 'WR-100',
    workflowId: 'patching',
    workflowName: 'Security Patching',
    target: '47 devices (CVE-2026-1234)',
    status: 'pending_approval',
    startedAt: null,
    completedAt: null,
    duration: '-',
    initiatedBy: 'Security Team',
    approvedBy: [],
    stepsCompleted: 0,
    stepsTotal: 5,
    dryRun: true,
    idempotencyKey: 'patch-cve20261234-20260111',
    auditEvents: 0,
    evidenceBundleId: null
  }
];

export const policies = [
  {
    id: 'POL-001',
    name: 'Encryption at Rest',
    description: 'All storage volumes must have encryption enabled',
    category: 'Security',
    severity: 'critical',
    enabled: true,
    rule: 'storage.encryption == true',
    findings: 3,
    compliant: 847,
    exceptions: 1
  },
  {
    id: 'POL-002',
    name: 'OS Version Minimum',
    description: 'Devices must run supported OS versions',
    category: 'Compliance',
    severity: 'high',
    enabled: true,
    rule: 'device.os_version >= minimum_supported_version',
    findings: 12,
    compliant: 2329,
    exceptions: 2
  },
  {
    id: 'POL-003',
    name: 'EDR Present',
    description: 'All endpoints must have EDR agent installed and active',
    category: 'Security',
    severity: 'critical',
    enabled: true,
    rule: 'device.edr_agent.status == "active"',
    findings: 5,
    compliant: 2336,
    exceptions: 0
  },
  {
    id: 'POL-004',
    name: 'MFA Required',
    description: 'All user accounts must have MFA enabled',
    category: 'Security',
    severity: 'critical',
    enabled: true,
    rule: 'user.mfa_enabled == true',
    findings: 8,
    compliant: 1515,
    exceptions: 3
  },
  {
    id: 'POL-005',
    name: 'Admin Rights Limited',
    description: 'Local admin rights restricted to approved users only',
    category: 'Security',
    severity: 'high',
    enabled: true,
    rule: 'device.local_admins.all(user => approved_admins.includes(user))',
    findings: 15,
    compliant: 2326,
    exceptions: 5
  },
  {
    id: 'POL-006',
    name: 'Password Rotation',
    description: 'Service account passwords must rotate every 90 days',
    category: 'Compliance',
    severity: 'medium',
    enabled: true,
    rule: 'service_account.password_age <= 90',
    findings: 7,
    compliant: 156,
    exceptions: 2
  }
];

export const policyFindings = [
  {
    id: 'FND-001',
    policyId: 'POL-003',
    policyName: 'EDR Present',
    resource: 'LT-1042',
    resourceType: 'Laptop',
    owner: 'Emily Zhang',
    team: 'Mobile Team',
    severity: 'critical',
    status: 'open',
    detectedAt: '2026-01-10T08:30:00Z',
    description: 'CrowdStrike agent not detected on device',
    remediation: 'Install CrowdStrike Falcon agent or quarantine device',
    autoRemediate: true
  },
  {
    id: 'FND-002',
    policyId: 'POL-001',
    policyName: 'Encryption at Rest',
    resource: 'staging-db',
    resourceType: 'RDS Instance',
    owner: 'Data Team',
    team: 'Data Team',
    severity: 'critical',
    status: 'open',
    detectedAt: '2026-01-09T14:00:00Z',
    description: 'Database storage encryption not enabled',
    remediation: 'Enable encryption on RDS instance (requires snapshot and restore)',
    autoRemediate: false
  },
  {
    id: 'FND-003',
    policyId: 'POL-004',
    policyName: 'MFA Required',
    resource: 'service-account-legacy',
    resourceType: 'Service Account',
    owner: 'Platform Team',
    team: 'Platform Team',
    severity: 'critical',
    status: 'exception',
    detectedAt: '2026-01-05T10:00:00Z',
    description: 'Service account does not support MFA',
    remediation: 'Migrate to modern authentication or document exception',
    autoRemediate: false
  },
  {
    id: 'FND-004',
    policyId: 'POL-002',
    policyName: 'OS Version Minimum',
    resource: 'DT-2847',
    resourceType: 'Desktop',
    owner: 'John Smith',
    team: 'Finance',
    severity: 'high',
    status: 'remediation_in_progress',
    detectedAt: '2026-01-08T11:00:00Z',
    description: 'Windows 10 version 1909 is end-of-life',
    remediation: 'Upgrade to Windows 11 or Windows 10 22H2',
    autoRemediate: true
  },
  {
    id: 'FND-005',
    policyId: 'POL-005',
    policyName: 'Admin Rights Limited',
    resource: 'LT-3421',
    resourceType: 'Laptop',
    owner: 'Dev User',
    team: 'Engineering',
    severity: 'high',
    status: 'open',
    detectedAt: '2026-01-10T16:00:00Z',
    description: 'Unapproved local admin account detected',
    remediation: 'Remove local admin rights or add to approved list',
    autoRemediate: false
  }
];

export const policyExceptions = [
  {
    id: 'EXC-001',
    policyId: 'POL-004',
    policyName: 'MFA Required',
    resource: 'service-account-legacy',
    reason: 'Legacy system does not support modern auth. Migration planned for Q2.',
    owner: 'Chris Martinez',
    team: 'Platform Team',
    approvedBy: 'Jordan Lee (Security)',
    createdAt: '2026-01-05T10:30:00Z',
    expiresAt: '2026-04-01T00:00:00Z',
    status: 'active'
  },
  {
    id: 'EXC-002',
    policyId: 'POL-005',
    policyName: 'Admin Rights Limited',
    resource: 'Developer workstations (Engineering)',
    reason: 'Developers require local admin for development tools and testing',
    owner: 'Sarah Chen',
    team: 'Engineering',
    approvedBy: 'VP Engineering',
    createdAt: '2025-12-01T00:00:00Z',
    expiresAt: '2026-06-01T00:00:00Z',
    status: 'active'
  },
  {
    id: 'EXC-003',
    policyId: 'POL-001',
    policyName: 'Encryption at Rest',
    resource: 'dev-storage-temp',
    reason: 'Temporary development storage, no sensitive data',
    owner: 'Pat Thompson',
    team: 'Data Team',
    approvedBy: 'Security Team',
    createdAt: '2026-01-02T00:00:00Z',
    expiresAt: '2026-02-01T00:00:00Z',
    status: 'active'
  }
];

export const auditEvents = [
  {
    id: 'AUD-10001',
    timestamp: '2026-01-11T10:05:23Z',
    type: 'workflow.step.completed',
    severity: 'info',
    source: 'Workflow Engine',
    actor: 'system',
    action: 'Create Okta account',
    target: 'jordan.kim@company.com',
    workflowRunId: 'WR-101',
    details: 'Successfully created Okta user account',
    connector: 'Okta'
  },
  {
    id: 'AUD-10000',
    timestamp: '2026-01-11T10:00:00Z',
    type: 'workflow.started',
    severity: 'info',
    source: 'Workflow Engine',
    actor: 'HR System (Workday)',
    action: 'Start workflow',
    target: 'Employee Onboarding - Jordan Kim',
    workflowRunId: 'WR-101',
    details: 'Workflow initiated for new hire onboarding',
    connector: null
  },
  {
    id: 'AUD-9999',
    timestamp: '2026-01-10T14:48:00Z',
    type: 'workflow.completed',
    severity: 'info',
    source: 'Workflow Engine',
    actor: 'system',
    action: 'Complete workflow',
    target: 'Employee Offboarding - Alex Rodriguez',
    workflowRunId: 'WR-104',
    details: 'All 7 steps completed successfully',
    connector: null
  },
  {
    id: 'AUD-9998',
    timestamp: '2026-01-10T14:45:00Z',
    type: 'connector.action',
    severity: 'info',
    source: 'Okta Connector',
    actor: 'system',
    action: 'Disable user',
    target: 'alex.rodriguez@company.com',
    workflowRunId: 'WR-104',
    details: 'User account disabled in Okta',
    connector: 'Okta'
  },
  {
    id: 'AUD-9997',
    timestamp: '2026-01-10T14:42:00Z',
    type: 'connector.action',
    severity: 'info',
    source: 'GitHub Connector',
    actor: 'system',
    action: 'Remove org member',
    target: 'alex-rodriguez',
    workflowRunId: 'WR-104',
    details: 'User removed from GitHub organization',
    connector: 'GitHub'
  },
  {
    id: 'AUD-9996',
    timestamp: '2026-01-10T14:38:00Z',
    type: 'approval.granted',
    severity: 'info',
    source: 'Approval Engine',
    actor: 'Jordan Lee (Security)',
    action: 'Approve workflow',
    target: 'WR-104',
    workflowRunId: 'WR-104',
    details: 'Security approval granted for offboarding workflow',
    connector: null
  },
  {
    id: 'AUD-9995',
    timestamp: '2026-01-10T14:35:00Z',
    type: 'approval.granted',
    severity: 'info',
    source: 'Approval Engine',
    actor: 'Lisa Park (HR)',
    action: 'Approve workflow',
    target: 'WR-104',
    workflowRunId: 'WR-104',
    details: 'HR approval granted for offboarding workflow',
    connector: null
  },
  {
    id: 'AUD-9994',
    timestamp: '2026-01-10T08:30:00Z',
    type: 'policy.violation',
    severity: 'critical',
    source: 'Policy Engine',
    actor: 'system',
    action: 'Detect violation',
    target: 'LT-1042',
    workflowRunId: null,
    details: 'EDR agent not detected on device',
    connector: 'CrowdStrike'
  },
  {
    id: 'AUD-9993',
    timestamp: '2026-01-07T16:50:00Z',
    type: 'workflow.completed',
    severity: 'warning',
    source: 'Workflow Engine',
    actor: 'system',
    action: 'Complete workflow',
    target: 'Device Quarantine - LT-1042',
    workflowRunId: 'WR-102',
    details: 'Device quarantine completed (emergency)',
    connector: null
  },
  {
    id: 'AUD-9992',
    timestamp: '2026-01-07T16:45:00Z',
    type: 'workflow.started',
    severity: 'warning',
    source: 'CrowdStrike Alert',
    actor: 'system',
    action: 'Auto-trigger workflow',
    target: 'LT-1042',
    workflowRunId: 'WR-102',
    details: 'Emergency quarantine triggered by missing EDR',
    connector: 'CrowdStrike'
  }
];

export const evidenceBundles = [
  {
    id: 'EB-2026-0142',
    name: 'Offboarding Evidence - Alex Rodriguez',
    type: 'workflow',
    relatedId: 'WR-104',
    createdAt: '2026-01-10T14:50:00Z',
    createdBy: 'system',
    status: 'complete',
    items: [
      { type: 'Workflow Run Record', count: 1 },
      { type: 'Approval Records', count: 2 },
      { type: 'Audit Events', count: 14 },
      { type: 'Connector Logs', count: 7 },
      { type: 'Screenshots', count: 0 },
      { type: 'Policy Checks', count: 3 }
    ],
    totalItems: 27,
    downloadUrl: '#'
  },
  {
    id: 'EB-2026-0138',
    name: 'License Reclamation Evidence - Figma',
    type: 'workflow',
    relatedId: 'WR-103',
    createdAt: '2026-01-08T09:20:00Z',
    createdBy: 'Sarah Chen',
    status: 'complete',
    items: [
      { type: 'Workflow Run Record', count: 1 },
      { type: 'Approval Records', count: 2 },
      { type: 'Audit Events', count: 8 },
      { type: 'License Changes', count: 23 },
      { type: 'User Notifications', count: 23 }
    ],
    totalItems: 57,
    downloadUrl: '#'
  },
  {
    id: 'EB-2026-0134',
    name: 'Quarantine Evidence - LT-1042',
    type: 'incident',
    relatedId: 'WR-102',
    createdAt: '2026-01-07T17:00:00Z',
    createdBy: 'system',
    status: 'complete',
    items: [
      { type: 'Workflow Run Record', count: 1 },
      { type: 'Audit Events', count: 10 },
      { type: 'CrowdStrike Alert', count: 1 },
      { type: 'Network Isolation Record', count: 1 },
      { type: 'PagerDuty Incident', count: 1 }
    ],
    totalItems: 14,
    downloadUrl: '#'
  }
];

// NL Query response plans for automation actions
export const automationPlans = {
  offboardAlexRodriguez: {
    query: 'Offboard Alex Rodriguez',
    type: 'workflow',
    workflowId: 'offboarding',
    target: {
      name: 'Alex Rodriguez',
      email: 'alex.rodriguez@company.com',
      team: 'Identity Team',
      role: 'Team Lead'
    },
    plan: [
      { step: 1, action: 'Disable Okta account', connector: 'Okta', impact: 'high' },
      { step: 2, action: 'Revoke GitHub access', connector: 'GitHub', impact: 'medium' },
      { step: 3, action: 'Deactivate Slack', connector: 'Slack', impact: 'medium' },
      { step: 4, action: 'Suspend Google Workspace', connector: 'Google', impact: 'high' },
      { step: 5, action: 'Transfer ownership', connector: 'Google', impact: 'medium' },
      { step: 6, action: 'Reclaim all licenses', connector: 'Vedaya', impact: 'low' },
      { step: 7, action: 'Archive user data', connector: 'Vedaya', impact: 'low' }
    ],
    policyChecks: [
      { policy: 'Data Retention', status: 'pass', note: '7-year retention configured' },
      { policy: 'Ownership Transfer', status: 'pass', note: 'Manager identified for handoff' },
      { policy: 'License Recovery', status: 'pass', note: '12 licenses will be reclaimed' }
    ],
    requiredApprovals: [
      { role: 'HR Manager', status: 'pending', approver: null },
      { role: 'Security Team', status: 'pending', approver: null }
    ],
    estimatedDuration: '20 min',
    dryRunAvailable: true,
    highImpact: true
  },
  quarantineLT1042: {
    query: 'Quarantine LT-1042 missing EDR',
    type: 'workflow',
    workflowId: 'quarantine',
    target: {
      name: 'LT-1042',
      type: 'Laptop',
      owner: 'Emily Zhang',
      team: 'Mobile Team',
      finding: 'EDR agent not detected'
    },
    plan: [
      { step: 1, action: 'Isolate from network', connector: 'CrowdStrike', impact: 'critical' },
      { step: 2, action: 'Disable VPN access', connector: 'Okta', impact: 'high' },
      { step: 3, action: 'Revoke OAuth tokens', connector: 'Okta', impact: 'high' },
      { step: 4, action: 'Create security incident', connector: 'PagerDuty', impact: 'medium' },
      { step: 5, action: 'Notify device owner', connector: 'Slack', impact: 'low' }
    ],
    policyChecks: [
      { policy: 'EDR Present (POL-003)', status: 'violation', note: 'This action remediates the violation' },
      { policy: 'Incident Response', status: 'pass', note: 'Following standard quarantine procedure' }
    ],
    requiredApprovals: [],
    estimatedDuration: '5 min',
    dryRunAvailable: false,
    highImpact: true,
    emergency: true
  },
  reclaimFigmaLicenses: {
    query: 'Reclaim Figma licenses under 50 percent utilization',
    type: 'workflow',
    workflowId: 'license-reclaim',
    target: {
      name: 'Figma',
      type: 'SaaS Application',
      totalLicenses: 200,
      activeLicenses: 67,
      utilization: '34%',
      inactiveUsers: 133
    },
    plan: [
      { step: 1, action: 'Identify inactive users (no login 30+ days)', connector: 'Figma', impact: 'low' },
      { step: 2, action: 'Send usage warning to 133 users', connector: 'Slack', impact: 'low' },
      { step: 3, action: 'Wait 7 days for response', connector: 'Vedaya', impact: 'low' },
      { step: 4, action: 'Revoke licenses from non-responders', connector: 'Figma', impact: 'medium' },
      { step: 5, action: 'Update license inventory', connector: 'Vedaya', impact: 'low' }
    ],
    policyChecks: [
      { policy: 'License Utilization', status: 'pass', note: 'Below 50% threshold triggers reclamation' },
      { policy: 'User Notification', status: 'pass', note: '7-day warning period configured' },
      { policy: 'Finance Approval', status: 'required', note: 'Cost impact: $36,000/year savings' }
    ],
    requiredApprovals: [
      { role: 'IT Manager', status: 'pending', approver: null },
      { role: 'Finance', status: 'pending', approver: null }
    ],
    estimatedDuration: '7 days',
    dryRunAvailable: true,
    highImpact: false,
    projectedSavings: '$36,000/year'
  },
  generateEvidenceBundle: {
    query: 'Generate evidence bundle for workflow run WR-104',
    type: 'evidence',
    workflowRunId: 'WR-104',
    workflowName: 'Employee Offboarding',
    target: 'Alex Rodriguez',
    completedAt: '2026-01-10T14:48:00Z',
    bundleContents: [
      { type: 'Workflow Run Record', description: 'Complete execution log with timestamps' },
      { type: 'Approval Records', description: '2 approvals (HR Manager, Security Team)' },
      { type: 'Audit Events', description: '14 detailed audit entries' },
      { type: 'Connector Logs', description: 'Action logs from 5 connectors' },
      { type: 'Policy Checks', description: '3 policy validations performed' },
      { type: 'Compliance Mapping', description: 'SOC 2 CC6.2, GDPR Art. 17 mappings' }
    ],
    exportFormats: ['PDF', 'JSON', 'CSV'],
    estimatedSize: '2.4 MB'
  }
};